import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Badge } from '../../../components/ui/badge';
import {
  AlertTriangle,
  BellRing,
  Calendar as CalendarIcon,
  Info,
  Loader2,
  Mail,
  Plus,
  Save,
  Send,
  Trash2,
  X,
} from 'lucide-react';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Build a textual preview of the next few reminders given the current form
 * values, mirroring the backend decision function so admins can sanity-check.
 */
const buildPreview = (form) => {
  const cap = form.max_reminders > 0 ? form.max_reminders : 5;
  const out = [];
  let day = form.first_reminder_offset_days;
  for (let i = 1; i <= cap; i += 1) {
    out.push({ number: i, day });
    day += form.repeat_interval_days;
  }
  return out;
};

const OverdueReminders = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [serverState, setServerState] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    first_reminder_offset_days: 1,
    repeat_interval_days: 7,
    max_reminders: 5,
    cc_admin: true,
    admin_recipients: [],
  });
  const [newEmail, setNewEmail] = useState('');

  const loadSettings = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const data = await adminService.getOverdueReminderSettings();
      setServerState(data);
      setForm({
        enabled: data.enabled,
        first_reminder_offset_days: data.first_reminder_offset_days,
        repeat_interval_days: data.repeat_interval_days,
        max_reminders: data.max_reminders,
        cc_admin: data.cc_admin,
        admin_recipients: data.admin_recipients || [],
      });
    } catch {
      setLoadError(true);
      toast.error('Failed to load overdue reminder settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const isDirty = useMemo(() => {
    if (!serverState) return false;
    return (
      form.enabled !== serverState.enabled ||
      Number(form.first_reminder_offset_days) !== serverState.first_reminder_offset_days ||
      Number(form.repeat_interval_days) !== serverState.repeat_interval_days ||
      Number(form.max_reminders) !== serverState.max_reminders ||
      form.cc_admin !== serverState.cc_admin ||
      JSON.stringify(form.admin_recipients) !== JSON.stringify(serverState.admin_recipients || [])
    );
  }, [serverState, form]);

  const preview = useMemo(() => buildPreview({
    first_reminder_offset_days: Number(form.first_reminder_offset_days) || 0,
    repeat_interval_days: Number(form.repeat_interval_days) || 1,
    max_reminders: Number(form.max_reminders) || 0,
  }), [form.first_reminder_offset_days, form.repeat_interval_days, form.max_reminders]);

  const handleAddEmail = () => {
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed) return;
    if (!EMAIL_RE.test(trimmed)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (form.admin_recipients.includes(trimmed)) {
      toast.error('That email is already in the list');
      return;
    }
    if (form.admin_recipients.length >= 10) {
      toast.error('Maximum 10 additional recipients');
      return;
    }
    setForm((f) => ({ ...f, admin_recipients: [...f.admin_recipients, trimmed] }));
    setNewEmail('');
  };

  const handleRemoveEmail = (email) => {
    setForm((f) => ({
      ...f,
      admin_recipients: f.admin_recipients.filter((e) => e !== email),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        first_reminder_offset_days: Number(form.first_reminder_offset_days),
        repeat_interval_days: Number(form.repeat_interval_days),
        max_reminders: Number(form.max_reminders),
        cc_admin: form.cc_admin,
        admin_recipients: form.admin_recipients,
      };
      const data = await adminService.updateOverdueReminderSettings(payload);
      setServerState(data);
      setForm({
        enabled: data.enabled,
        first_reminder_offset_days: data.first_reminder_offset_days,
        repeat_interval_days: data.repeat_interval_days,
        max_reminders: data.max_reminders,
        cc_admin: data.cc_admin,
        admin_recipients: data.admin_recipients || [],
      });
      toast.success('Overdue reminder settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!serverState) return;
    setForm({
      enabled: serverState.enabled,
      first_reminder_offset_days: serverState.first_reminder_offset_days,
      repeat_interval_days: serverState.repeat_interval_days,
      max_reminders: serverState.max_reminders,
      cc_admin: serverState.cc_admin,
      admin_recipients: serverState.admin_recipients || [],
    });
    setNewEmail('');
  };

  const handleTriggerNow = async () => {
    setTriggering(true);
    try {
      const result = await adminService.triggerOverdueReminderCron();
      toast.success(
        `Cron run complete — ${result.reminders_sent} reminder${result.reminders_sent === 1 ? '' : 's'} sent ` +
        `(${result.candidates_scanned} invoice${result.candidates_scanned === 1 ? '' : 's'} scanned).`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run cron');
    } finally {
      setTriggering(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  }

  if (loadError && !serverState) {
    return (
      <div className="space-y-6" data-testid="overdue-reminders-page">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            Overdue Invoice Reminders
          </h2>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div
              className="flex items-start gap-3 p-4 border border-red-200 bg-red-50 rounded-lg"
              data-testid="overdue-load-error"
            >
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-red-900">
                  Couldn't load overdue reminder settings
                </p>
                <p className="text-sm text-red-800 mt-1">
                  This usually means your session expired or the network glitched. Please try again.
                </p>
              </div>
              <Button
                onClick={loadSettings}
                variant="outline"
                className="border-red-300"
                data-testid="overdue-retry-load-btn"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="overdue-reminders-page">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="h-6 w-6 text-red-600" />
          Overdue Invoice Reminders
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-2xl">
          Automatically remind clients (and your team) about unpaid invoices that
          have passed their due date. The due date itself is computed from each
          client's <strong>payment terms</strong> (e.g., Net 30) — these settings
          control only how long to wait, how often to follow up, and who gets
          copied.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BellRing className="h-5 w-5 text-amber-600" /> Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div>
                <Label htmlFor="enabled-toggle" className="font-semibold cursor-pointer">
                  Enable automated overdue reminders
                </Label>
                <p className="text-xs text-gray-500 mt-1 max-w-md">
                  When off, the daily cron job will skip all overdue sends. Manual
                  reminders from the Invoices page still work normally.
                </p>
              </div>
              <Switch
                id="enabled-toggle"
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
                data-testid="overdue-enabled-toggle"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="first-offset">First reminder (days past due)</Label>
                <Input
                  id="first-offset"
                  type="number"
                  min={0}
                  max={60}
                  step={1}
                  value={form.first_reminder_offset_days}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, first_reminder_offset_days: e.target.value }))
                  }
                  data-testid="first-offset-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How many days <em>after</em> the due date to send reminder #1.
                  Use 0 to fire the day an invoice first becomes overdue.
                </p>
              </div>

              <div>
                <Label htmlFor="repeat-interval">Follow-up cadence (days)</Label>
                <Input
                  id="repeat-interval"
                  type="number"
                  min={1}
                  max={90}
                  step={1}
                  value={form.repeat_interval_days}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, repeat_interval_days: e.target.value }))
                  }
                  data-testid="repeat-interval-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How often to follow up after the first reminder (e.g., 7 = weekly).
                </p>
              </div>

              <div>
                <Label htmlFor="max-reminders">Max reminders per invoice</Label>
                <Input
                  id="max-reminders"
                  type="number"
                  min={0}
                  max={20}
                  step={1}
                  value={form.max_reminders}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, max_reminders: e.target.value }))
                  }
                  data-testid="max-reminders-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Cap the total reminders sent per invoice. Use <strong>0</strong> to
                  keep sending until the invoice is paid.
                </p>
              </div>
            </div>

            {/* Live preview */}
            <div
              className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
              data-testid="overdue-reminder-preview"
            >
              <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-2 text-sm">
                <p className="font-medium text-amber-900">Reminder schedule preview</p>
                <p className="text-amber-900/80 text-xs">
                  For a sample invoice that becomes overdue today, reminders would fire on:
                </p>
                <ul className="text-amber-900 text-xs space-y-0.5">
                  {preview.map((p) => (
                    <li key={p.number} data-testid={`preview-row-${p.number}`}>
                      <strong>#{p.number}</strong> — day {p.day} past due
                    </li>
                  ))}
                </ul>
                {Number(form.max_reminders) === 0 && (
                  <p className="text-amber-800/70 text-xs italic">
                    (Showing first 5 — reminders continue every {form.repeat_interval_days} days until paid.)
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5 text-amber-600" /> Notification Recipients
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <p className="text-sm text-gray-600 max-w-2xl">
              The client is always the primary recipient. Use these options to copy
              your internal team or external bookkeepers.
            </p>

            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div>
                <Label htmlFor="cc-admin-toggle" className="font-semibold cursor-pointer">
                  CC all admin users
                </Label>
                <p className="text-xs text-gray-500 mt-1">
                  Adds every active admin user's email as a CC on each reminder.
                </p>
              </div>
              <Switch
                id="cc-admin-toggle"
                checked={form.cc_admin}
                onCheckedChange={(v) => setForm((f) => ({ ...f, cc_admin: v }))}
                data-testid="overdue-cc-admin-toggle"
              />
            </div>

            <div>
              <Label>Additional recipients</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Up to 10 extra email addresses (e.g., your bookkeeper or finance team).
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="bookkeeper@firm.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  data-testid="new-recipient-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddEmail}
                  data-testid="add-recipient-btn"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
              </div>

              {form.admin_recipients.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="recipient-list">
                  {form.admin_recipients.map((email) => (
                    <Badge
                      key={email}
                      variant="secondary"
                      className="text-sm py-1 pl-3 pr-2 gap-1"
                      data-testid={`recipient-pill-${email}`}
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="ml-1 hover:bg-gray-300 rounded-full p-0.5"
                        aria-label={`Remove ${email}`}
                        data-testid={`remove-recipient-${email}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              {serverState?.updated_at && (
                <div className="flex items-center gap-2 text-xs text-gray-500" data-testid="overdue-meta">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Last updated {formatDateTime(serverState.updated_at)}
                  {serverState.updated_by_email && (
                    <Badge variant="outline" className="ml-1">{serverState.updated_by_email}</Badge>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleTriggerNow}
                disabled={!serverState?.enabled || triggering || isDirty}
                title={isDirty ? 'Save changes first' : !serverState?.enabled ? 'Enable the feature first' : ''}
                data-testid="run-cron-btn"
              >
                {triggering ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Run Now</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={handleRevert}
                disabled={!isDirty || saving}
                data-testid="revert-overdue-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Revert
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="bg-red-600 hover:bg-red-700"
                data-testid="save-overdue-btn"
              >
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Changes</>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverdueReminders;
